import React, { useState, useEffect, useRef, useMemo } from "react";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { ChatBubble } from "./ChatBubble";
import { ResponseOptions } from "./ResponseOptions";
import {
  CompanySize,
  UserRole,
  MainObjective,
  ReferralSource,
  LeadVolume,
  ContactMethod,
  InstantAnswerAvailable,
  CrmSystem,
  DailyContactVolume,
  OnboardingData,
  OnboardingProfile,
} from "@/types/onboarding.types";
import { Button } from "@/components/ui/button";
import { ArrowLeft, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { industryService } from "@/services/industryService";
import { onboardingService } from "@/services/onboardingService";
import { welcomeFormService } from "@/services/welcomeFormService";
import { brandService } from "@/services/brandService";
import { useAuth } from "@/contexts/AuthContext";
import { FuturisticLoader } from "@/components/ui/FuturisticLoader";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";

interface ConversationStep {
  id: string;
  message: string;
  type:
    | "buttons"
    | "select"
    | "multi-select"
    | "text"
    | "branding"
    | "ai-pitch"
    | "welcome-form"
    | "persona-cards";
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
}

// Hardcoded persona data for onboarding
const ONBOARDING_PERSONAS = [
  {
    id: "lexi",
    name: "Lexi (☺️ Myself )",
    title: "Lead Generation Wizard",
    description:
      "Engage your website visitors 24/7, qualifies them, schedules appointments, etc.",
    avatar: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/avatars/Lexi%20micro%20gif.gif`,
  },
  {
    id: "cory",
    name: "Cory",
    title: "Customer Support Mastermind",
    description: "Supports your customers by answering their questions 24/7",
    avatar: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/avatars/Codi%20-%20micro%20gif.gif`,
  },
];

// Utility function to extract domain from email and prepopulate website
const getDomainFromEmail = (email: string): string | null => {
  try {
    const emailParts = email.split("@");
    if (emailParts.length !== 2) return null;

    const domain = emailParts[1].toLowerCase();

    // Skip common email providers
    const commonProviders = [
      "gmail.com",
      "yahoo.com",
      "outlook.com",
      "hotmail.com",
      "icloud.com",
      "aol.com",
    ];
    if (commonProviders.includes(domain)) {
      return null;
    }

    return `https://${domain}`;
  } catch (error) {
    return null;
  }
};

export function ModernOnboardingFlow() {
  const { completeOnboarding } = useOnboarding();
  const { organization, user, refreshProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showMessage, setShowMessage] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [multiSelectValues, setMultiSelectValues] = useState<
    Record<string, string[]>
  >({});

  // Start with isLoading: false since OnboardingPage preloads all data
  const [isLoading, setIsLoading] = useState(false);
  const [industries, setIndustries] = useState<any[]>([]);

  // Start with industriesLoading: false since data is preloaded
  const [industriesLoading, setIndustriesLoading] = useState(false);

  // New state for website and branding
  const [websiteText, setWebsiteText] = useState("");
  const [brandingData, setBrandingData] = useState<{
    brand_color?: string;
    logo_file?: File | null;
    logo_url?: string;
    logo_preview_url?: string; // For immediate preview
  }>({
    brand_color: "#3b82f6",
    logo_file: null,
    logo_url: null,
    logo_preview_url: null,
  });

  // New state for AI pitch
  const [aiPitchMessage, setAiPitchMessage] = useState("");
  const [isGeneratingPitch, setIsGeneratingPitch] = useState(false);

  // New state for welcome form
  const [welcomeForm, setWelcomeForm] = useState({
    firstName: "",
    lastName: "",
    organizationName: "",
  });

  // Submission state guards to prevent race conditions
  const [submissionStates, setSubmissionStates] = useState<
    Record<string, boolean>
  >({});
  const [welcomeSubmitting, setWelcomeSubmitting] = useState(false);
  const [textSubmitting, setTextSubmitting] = useState(false);
  const [brandingSubmitting, setBrandingSubmitting] = useState(false);
  const [aiPitchSubmitting, setAiPitchSubmitting] = useState(false);

  // Organization setup states for website step
  const [isSettingUpOrganization, setIsSettingUpOrganization] = useState(false);

  // Flag to prevent step recalculation during submissions (fixes navigation bounce)
  const [isSubmittingStep, setIsSubmittingStep] = useState(false);

  // Error state management for better UX
  const [errorStates, setErrorStates] = useState<Record<string, string | null>>(
    {},
  );
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [currentError, setCurrentError] = useState<string | null>(null);

  // Track which steps have completed their typing animation to prevent re-animation
  const [completedAnimations, setCompletedAnimations] = useState<Set<string>>(
    new Set(),
  );

  // Store timeout IDs for cleanup
  const timeoutIds = useRef<NodeJS.Timeout[]>([]);
  // Store current preview URL for proper cleanup
  const currentPreviewUrl = useRef<string | null>(null);

  // Add persona selection state
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null);

  // Modular validation functions
  const validateWelcomeForm = (data: {
    firstName: string;
    lastName: string;
    organizationName: string;
  }) => {
    return (
      Boolean(data.firstName.trim()) &&
      Boolean(data.lastName.trim()) &&
      Boolean(data.organizationName.trim())
    );
  };

  const validateRequiredFields = (stepId: string, value: any) => {
    switch (stepId) {
      case "welcome":
        return typeof value === "object" && validateWelcomeForm(value);
      case "website":
        return typeof value === "string" && value.trim().length > 0;
      case "ai_pitch":
        return !!aiPitchMessage;
      default:
        return true; // Other steps don't require validation
    }
  };

  // Modular step handlers
  const handleWelcomeFormSubmission = async (
    stepId: string,
    formData: { firstName: string; lastName: string; organizationName: string },
  ) => {
    if (!validateWelcomeForm(formData)) {
      showError("All fields are required for welcome form", stepId);
      return false;
    }

    if (welcomeSubmitting) {
      console.log(
        "Welcome form is already being submitted, ignoring duplicate request",
      );
      return false;
    }

    clearError(stepId);
    setWelcomeSubmitting(true);
    setSubmissionStates((prev) => ({ ...prev, [stepId]: true }));

    try {
      const newResponses = { ...responses, [stepId]: formData };
      setResponses(newResponses);
      await saveStepData(stepId, formData, newResponses);
      return true;
    } catch (error) {
      console.error("Welcome form submission failed:", error);
      showError("Failed to save your information. Please try again.", stepId);
      return false;
    } finally {
      setWelcomeSubmitting(false);
      setSubmissionStates((prev) => ({ ...prev, [stepId]: false }));
    }
  };

  // Helper function to persist only the current step without UI changes
  const persistCurrentStepOnly = async (newStepNumber: number) => {
    if (!organization?.id) return;

    try {
      // Get current DB step to prevent going backwards
      const currentDbStep =
        (organization.onboarding_meta as any)?.current_step || 1;

      // Only update if we're advancing or staying on the same step
      if (newStepNumber >= currentDbStep) {
        const result = await onboardingService.updateOnboardingData(
          organization.id,
          {}, // No data to save, just step progression
          newStepNumber,
          CONVERSATION_STEPS.length,
          false,
        );

        if (result.error) {
          console.error("Error persisting current step:", result.error);
        } else {
          console.log("Current step persisted successfully:", newStepNumber);
        }
      } else {
        console.log(
          `Prevented going backwards: DB step ${currentDbStep} > new step ${newStepNumber}`,
        );
      }
    } catch (error) {
      console.error("Error in persistCurrentStepOnly:", error);
      // Don't throw - this is not critical for user flow
    }
  };

  const handleTextSubmission = async (stepId: string, textValue: string) => {
    if (textSubmitting) {
      console.log(
        "Text form is already being submitted, ignoring duplicate request",
      );
      return false;
    }

    clearError(stepId);
    setIsSubmittingStep(true); // Prevent step recalculation during submission
    setTextSubmitting(true);
    setSubmissionStates((prev) => ({ ...prev, [stepId]: true }));

    try {
      // If this is the website step, handle organization setup and complete onboarding
      if (stepId === "website" && textValue && organization?.id) {
        console.log("Website step completed, starting organization setup...");
        setIsSettingUpOrganization(true);

        try {
          // Update organization website field first
          const { error: updateError } = await supabase
            .from("organizations")
            .update({ website: textValue })
            .eq("id", organization.id);

          if (updateError) {
            console.error("Error updating organization website:", updateError);
            throw new Error("Failed to save website URL");
          }

          // Refresh profile to get updated organization
          await refreshProfile();

          // Save the website data to responses immediately to prevent state loss
          const newResponses = { ...responses, [stepId]: textValue };
          setResponses(newResponses);

          // Now call brand fetch service (non-blocking)
          try {
            console.log("Calling brand fetch for organization setup...");
            const result = await brandService.fetchBrandData(organization.id);

            if (result.success) {
              console.log("Organization setup completed successfully");
            } else {
              console.warn(
                "Brand fetch failed, but continuing onboarding:",
                result.error,
              );
              // Don't fail the onboarding if brand fetch fails - it's optional enhancement
            }
          } catch (brandError) {
            console.warn(
              "Brand fetch service failed, continuing onboarding:",
              brandError,
            );
            // Brand fetch failure should not block onboarding completion
          }

          // Mark onboarding as complete and redirect to payment
          console.log("Completing onboarding and redirecting to payment...");
          await completeOnboarding();
        } catch (error) {
          console.error("Organization setup failed:", error);

          // Save the website data to responses even if setup fails to prevent data loss
          try {
            const newResponses = { ...responses, [stepId]: textValue };
            setResponses(newResponses);
          } catch (responseError) {
            console.error("Failed to save responses:", responseError);
          }

          // Don't fail the onboarding, just log the error
          showError(
            "We couldn't automatically set up your organization, but that's okay! You can continue with onboarding.",
            stepId,
          );
          // Still complete onboarding even if setup fails
          try {
            await completeOnboarding();
          } catch (completionError) {
            console.error("Failed to complete onboarding:", completionError);
            // If completion fails, show a more specific error but don't reset to step 1
            showError(
              "There was an issue completing your onboarding. Please refresh the page and try again.",
              stepId,
            );
          }
        } finally {
          setIsSettingUpOrganization(false);
        }
      } else {
        // For non-website steps, use regular flow
        const newResponses = { ...responses, [stepId]: textValue };
        setResponses(newResponses);
        await saveStepData(stepId, textValue, newResponses);
      }

      return true;
    } catch (error) {
      console.error("Text submission failed:", error);
      showError(
        "Failed to save your website information. Please try again.",
        stepId,
      );
      return false;
    } finally {
      setTextSubmitting(false);
      setSubmissionStates((prev) => ({ ...prev, [stepId]: false }));
      setIsSubmittingStep(false); // Re-enable step recalculation
    }
  };

  const handleBrandingSubmission = async (
    stepId: string,
    brandingValue: any,
  ) => {
    if (brandingSubmitting) {
      console.log(
        "Branding form is already being submitted, ignoring duplicate request",
      );
      return false;
    }

    clearError(stepId);
    setBrandingSubmitting(true);
    setSubmissionStates((prev) => ({ ...prev, [stepId]: true }));

    try {
      const newResponses = { ...responses, [stepId]: brandingValue };
      setResponses(newResponses);
      await saveStepData(stepId, brandingValue, newResponses);
      return true;
    } catch (error) {
      console.error("Branding submission failed:", error);
      showError(
        "Failed to save your branding information. Please try again.",
        stepId,
      );
      return false;
    } finally {
      setBrandingSubmitting(false);
      setSubmissionStates((prev) => ({ ...prev, [stepId]: false }));
    }
  };

  const handleAIPitchSubmission = async (stepId: string) => {
    if (aiPitchSubmitting) {
      console.log(
        "AI pitch is already being submitted, ignoring duplicate request",
      );
      return false;
    }

    if (!aiPitchMessage) {
      showError("AI pitch is still generating. Please wait a moment.", stepId);
      return false;
    }

    clearError(stepId);
    setAiPitchSubmitting(true);
    setSubmissionStates((prev) => ({ ...prev, [stepId]: true }));

    try {
      if (aiPitchMessage && !responses.ai_pitch) {
        const newResponses = { ...responses, ai_pitch: aiPitchMessage };
        setResponses(newResponses);
        await saveStepData(stepId, aiPitchMessage, newResponses);
      } else {
        await advanceToNextStep();
      }
      return true;
    } catch (error) {
      console.error("AI pitch submission failed:", error);
      showError("Failed to save your plan. Please try again.", stepId);
      return false;
    } finally {
      setAiPitchSubmitting(false);
      setSubmissionStates((prev) => ({ ...prev, [stepId]: false }));
    }
  };

  const handleGenericSubmission = async (stepId: string, value: any) => {
    // Prevent selection of loading option
    if (stepId === "industry" && value === "loading") {
      console.log("Ignoring loading option selection");
      return false;
    }

    clearError(stepId);
    setSubmissionStates((prev) => ({ ...prev, [stepId]: true }));

    try {
      const newResponses = { ...responses, [stepId]: value };
      setResponses(newResponses);
      await saveStepData(stepId, value, newResponses);
      return true;
    } catch (error) {
      console.error("Generic submission failed:", error);
      showError("Failed to save your selection. Please try again.", stepId);
      return false;
    } finally {
      setSubmissionStates((prev) => ({ ...prev, [stepId]: false }));
    }
  };

  // Error handling utilities
  const showError = (message: string, stepId?: string) => {
    setCurrentError(message);
    setShowErrorMessage(true);
    if (stepId) {
      setErrorStates((prev) => ({ ...prev, [stepId]: message }));
    }

    // Auto-hide error after 5 seconds
    addTimeout(() => {
      setShowErrorMessage(false);
      setCurrentError(null);
      if (stepId) {
        setErrorStates((prev) => ({ ...prev, [stepId]: null }));
      }
    }, 5000);
  };

  const clearError = (stepId?: string) => {
    setShowErrorMessage(false);
    setCurrentError(null);
    if (stepId) {
      setErrorStates((prev) => ({ ...prev, [stepId]: null }));
    }
  };

  // Enhanced loading state management
  const getStepLoadingMessage = (stepId: string) => {
    switch (stepId) {
      case "welcome":
        return "Saving your information...";
      case "website":
        return isSettingUpOrganization
          ? "Hubflo AI agents are visiting your website to extract information and brand assets... Almost done!"
          : "Analyzing your website...";
      case "branding":
        return "Uploading your brand assets...";
      case "ai_pitch":
        return "Generating your personalized plan...";
      default:
        return "Saving your preferences...";
    }
  };

  const isStepLoading = (stepId: string) => {
    const stepSpecificLoadingStates: Record<string, boolean> = {
      welcome: welcomeSubmitting,
      website: textSubmitting || isSettingUpOrganization,
      branding: brandingSubmitting,
      ai_pitch: aiPitchSubmitting,
    };
    return (
      submissionStates[stepId] || stepSpecificLoadingStates[stepId] || false
    );
  };

  // Load industries immediately from preloaded data
  useEffect(() => {
    // Check for preloaded industries in sessionStorage first
    const preloadedIndustries = sessionStorage.getItem("preloadedIndustries");
    if (preloadedIndustries) {
      const industriesData = JSON.parse(preloadedIndustries);
      console.log(
        "[ModernOnboardingFlow] Using preloaded industries:",
        industriesData.length,
      );
      setIndustries(industriesData);
      // Don't need to set industriesLoading since it starts false
    } else {
      console.error(
        "[ModernOnboardingFlow] ERROR: No preloaded industries found! This should not happen.",
      );
      // Fallback - load from API
      const loadIndustries = async () => {
        try {
          setIndustriesLoading(true);
          const { data, error } = await industryService.getAllIndustries();
          if (error) {
            console.error("Failed to load industries:", error);
          } else {
            setIndustries(data || []);
          }
        } catch (error) {
          console.error("Error loading industries:", error);
        } finally {
          setIndustriesLoading(false);
        }
      };
      loadIndustries();
    }
  }, []);

  // Memoize CONVERSATION_STEPS to prevent recreation on every render
  const CONVERSATION_STEPS: ConversationStep[] = useMemo(
    () => [
      {
        id: "welcome",
        message:
          "Welcome to Hubflo Agents! Before we get started, I need a few key information please.",
        type: "welcome-form",
      },
      {
        id: "industry",
        message: "Thanks for that! What industry is your business in?",
        type: "select",
        placeholder: "Select your industry",
        options: industriesLoading
          ? [{ value: "loading", label: "Loading industries..." }]
          : industries.map((industry) => ({
              value: industry.name,
              label: industryService.getDisplayName(industry),
            })),
      },
      {
        id: "website",
        message:
          "What's your website URL? This helps us train your agent with your business information.",
        type: "text",
        placeholder: "https://yourwebsite.com",
      },
    ],
    [industries, industriesLoading],
  );

  const currentStep = CONVERSATION_STEPS[currentStepIndex];
  const isLastStep = currentStepIndex === CONVERSATION_STEPS.length - 1;
  const isFirstStep = currentStepIndex === 0;

  // Add ref to track if data has been initialized to prevent reloads
  const dataInitializedRef = useRef(false);

  // Load existing data on component mount
  useEffect(() => {
    console.log("[ModernOnboardingFlow] useEffect triggered with:", {
      organization: !!organization,
      user: !!user,
      industriesLength: industries.length,
      isLoading,
      isSubmittingStep,
      dataInitialized: dataInitializedRef.current,
    });

    // Don't recalculate steps during active submissions (prevents navigation bounce)
    if (isSubmittingStep) {
      console.log(
        "[ModernOnboardingFlow] Skipping step recalculation - submission in progress",
      );
      return;
    }

    // Don't reload data if it's already been initialized (prevents reloads during onboarding)
    if (dataInitializedRef.current) {
      console.log(
        "[ModernOnboardingFlow] Data already initialized, preventing reload",
      );
      return;
    }

    if (!organization || !user || industries.length === 0) {
      console.log("[ModernOnboardingFlow] Missing dependencies, waiting...", {
        hasOrganization: !!organization,
        hasUser: !!user,
        industriesCount: industries.length,
      });
      return; // Wait for all dependencies to be loaded
    }

    console.log(
      "[ModernOnboardingFlow] All dependencies available, loading data...",
    );

    const loadExistingData = async () => {
      console.log("Loading existing onboarding data from preloaded sources...");

      try {
        const existingResponses: Record<string, any> = {};
        const existingMultiSelect: Record<string, string[]> = {};

        // Load welcome form data - check for preloaded data first
        const preloadedWelcomeForm = sessionStorage.getItem(
          "preloadedWelcomeForm",
        );
        let welcomeData = null;

        if (preloadedWelcomeForm) {
          welcomeData = JSON.parse(preloadedWelcomeForm);
          console.log(
            "[ModernOnboardingFlow] Using preloaded welcome form data:",
            welcomeData,
          );
        } else {
          console.log(
            "[ModernOnboardingFlow] Loading welcome form from API...",
          );
          const welcomeResult = await welcomeFormService.loadWelcomeFormData(
            user.id,
            organization.id,
          );
          welcomeData = welcomeResult.success ? welcomeResult.data : null;
        }

        if (welcomeData) {
          const { firstName, lastName, organizationName } = welcomeData;

          // Set welcome form state - organizationName always starts empty for UX
          setWelcomeForm({
            firstName: firstName || "",
            lastName: lastName || "",
            organizationName: "", // Always start empty regardless of saved data
          });

          // Mark welcome step as completed if we have firstName and lastName
          // This ensures proper step navigation while keeping org field empty for UX
          if (firstName && lastName) {
            existingResponses.welcome = {
              firstName,
              lastName,
              organizationName: organizationName || "", // Use saved org name for step completion
            };
            console.log(
              "[ModernOnboardingFlow] Welcome step marked as completed for navigation, but org field will display empty",
            );
          }
        } else {
          // Initialize empty welcome form
          setWelcomeForm({
            firstName: "",
            lastName: "",
            organizationName: "",
          });
        }

        // Company size step removed from onboarding flow

        // Load industry
        if (organization.industry_id) {
          const industry = industries.find(
            (ind) => ind.id === organization.industry_id,
          );
          if (industry) {
            existingResponses.industry = industry.name;
          }
        }

        // Load referral_source
        if (organization.referral_source) {
          existingResponses.referral_source = organization.referral_source;
        }

        // Load onboarding_profile data (only for steps that still exist)
        const profile = organization.onboarding_profile as any;
        if (profile) {
          // Load crm_systems (now single select)
          if (profile.crm_systems) {
            // Handle both legacy array format and new single value format
            const crmValue = Array.isArray(profile.crm_systems)
              ? profile.crm_systems[0]
              : profile.crm_systems;
            existingResponses.crm_systems = crmValue;
          }

          // Load daily_contact_volume (still exists)
          if (profile.daily_contact_volume) {
            existingResponses.daily_contact_volume =
              profile.daily_contact_volume;
          }

          // Load website and branding data (still exists)
          if (profile.website) {
            existingResponses.website = profile.website;
            setWebsiteText(profile.website);
          }

          if (profile.brand_color) {
            existingResponses.branding = { brand_color: profile.brand_color };
            setBrandingData((prev) => ({
              ...prev,
              brand_color: profile.brand_color,
            }));
          }

          // Load AI pitch message if exists (still exists)
          if (profile.ai_pitch_message) {
            existingResponses.ai_pitch = profile.ai_pitch_message;
            setAiPitchMessage(profile.ai_pitch_message);
          }
        }

        // Load website from organization if not in profile
        if (organization.website && !existingResponses.website) {
          existingResponses.website = organization.website;
          setWebsiteText(organization.website);
        }

        // Load branding data from organization
        if (organization.brand_color && !existingResponses.branding) {
          existingResponses.branding = {
            brand_color: organization.brand_color,
          };
          setBrandingData((prev) => ({
            ...prev,
            brand_color: organization.brand_color,
          }));
        }

        if (organization.logo_url) {
          setBrandingData((prev) => ({
            ...prev,
            logo_url: organization.logo_url,
          }));
        }

        // Prepopulate website from email domain if no website exists
        if (!existingResponses.website && user?.email) {
          const suggestedWebsite = getDomainFromEmail(user.email);
          if (suggestedWebsite) {
            setWebsiteText(suggestedWebsite);
          }
        }

        // Simplified approach: Always start from step 1 (index 0)
        // Pre-populate form fields with existing data for smooth UX
        console.log(
          "[ModernOnboardingFlow] Starting from step 1 with pre-populated data",
        );
        console.log(
          "[ModernOnboardingFlow] Existing responses:",
          existingResponses,
        );
        console.log(
          "[ModernOnboardingFlow] Existing multi-select:",
          existingMultiSelect,
        );

        setResponses(existingResponses);
        setMultiSelectValues(existingMultiSelect);
        setCurrentStepIndex(0); // Always start from step 1
        console.log("[ModernOnboardingFlow] Data ready, starting from step 1");

        // Mark data as initialized to prevent reloads
        dataInitializedRef.current = true;
        console.log(
          "[ModernOnboardingFlow] Data initialization completed (no loading state changes)",
        );
      } catch (error) {
        console.error("Error loading existing data:", error);
        // Continue with empty state if loading fails
        console.log(
          "[ModernOnboardingFlow] Error occurred, continuing without loading states",
        );

        // Mark data as initialized even on error to prevent reload loops
        dataInitializedRef.current = true;
        console.log(
          "[ModernOnboardingFlow] Data initialization completed (with errors, no loading state changes)",
        );
      }
    };

    loadExistingData();
  }, [
    organization,
    user,
    industries,
    // Removed unstable dependencies to fix infinite loop:
    // CONVERSATION_STEPS - already memoized and only depends on industries
    // completeOnboarding - not used in this effect
    // isSubmittingStep - handled with early return above
  ]);

  // Additional debug log for render conditions (commented out to reduce console noise)
  // console.log("[ModernOnboardingFlow] Render state:", {
  //   isLoading,
  //   isProcessing,
  //   currentStepIndex,
  //   hasCurrentStep: !!currentStep,
  //   organization: !!organization,
  //   user: !!user,
  //   industriesLength: industries.length,
  // });

  // Cleanup function to clear all timeouts
  const clearAllTimeouts = () => {
    timeoutIds.current.forEach((id) => clearTimeout(id));
    timeoutIds.current = [];
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts();
      // Cleanup preview URL to prevent memory leaks
      if (currentPreviewUrl.current) {
        URL.revokeObjectURL(currentPreviewUrl.current);
        currentPreviewUrl.current = null;
      }
    };
  }, []);

  // Helper function to add timeout with cleanup tracking
  const addTimeout = (callback: () => void, delay: number) => {
    const id = setTimeout(() => {
      // Remove this timeout from tracking array
      timeoutIds.current = timeoutIds.current.filter(
        (timeoutId) => timeoutId !== id,
      );
      callback();
    }, delay);
    timeoutIds.current.push(id);
    return id;
  };

  // Function to generate AI pitch
  const generateAIPitch = async () => {
    if (!organization?.id || aiPitchMessage || isGeneratingPitch) {
      return; // Already have message or currently generating
    }

    setIsGeneratingPitch(true);

    // Create AbortController for cleanup
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      // Get user's JWT token for authentication
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-onboarding-pitch`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            organizationId: organization.id,
          }),
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error("Failed to generate pitch");
      }

      const data = await response.json();
      setAiPitchMessage(data.message);

      // Use functional state update to avoid stale closures
      setResponses((prevResponses) => ({
        ...prevResponses,
        ai_pitch: data.message,
      }));
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === "AbortError") {
        console.log("AI pitch generation was cancelled");
        return;
      }

      console.error("Error generating AI pitch:", error);
      // Set fallback message
      const fallbackMessage = `Thank you for this context. **Hubflo AI agents can transform your business operations** 🚀

**We can help capture more leads, provide instant responses to your customers, and integrate seamlessly with your current workflow.** This means more qualified prospects and more time for you to focus on growing your business.

Ready to unlock the power of AI for your business? ✨`;

      setAiPitchMessage(fallbackMessage);
      // Use functional state update here too
      setResponses((prevResponses) => ({
        ...prevResponses,
        ai_pitch: fallbackMessage,
      }));
    } finally {
      setIsGeneratingPitch(false);
    }
  };

  // Start the conversation (only after loading is complete)
  useEffect(() => {
    if (!isLoading) {
      addTimeout(() => {
        setShowMessage(true);

        // If current step animation was already completed, show options immediately
        if (currentStep && completedAnimations.has(currentStep.id)) {
          addTimeout(() => {
            // For AI pitch step, only show options when message is ready
            if (currentStep.id === "ai_pitch") {
              if (aiPitchMessage && !isGeneratingPitch) {
                setShowOptions(true);
              }
            } else {
              setShowOptions(true);
            }
          }, 300);
        }
      }, 500);
    }
  }, [
    isLoading,
    currentStep?.id,
    completedAnimations,
    aiPitchMessage,
    isGeneratingPitch,
  ]);

  // Generate AI pitch when reaching the ai_pitch step
  useEffect(() => {
    if (
      currentStep?.id === "ai_pitch" &&
      !aiPitchMessage &&
      !isGeneratingPitch
    ) {
      generateAIPitch();
    }
  }, [currentStep?.id, aiPitchMessage, isGeneratingPitch]);

  // Show options when AI pitch message is ready
  useEffect(() => {
    if (
      currentStep?.id === "ai_pitch" &&
      aiPitchMessage &&
      showMessage &&
      !showOptions
    ) {
      addTimeout(() => {
        setShowOptions(true);
      }, 1000); // Small delay to let users read the message
    }
  }, [currentStep?.id, aiPitchMessage, showMessage, showOptions]);

  const handleTypingComplete = () => {
    // Mark this step's animation as completed
    if (currentStep?.id) {
      setCompletedAnimations((prev) => new Set([...prev, currentStep.id]));
    }

    addTimeout(() => {
      // For AI pitch step, only show options when message is ready
      if (currentStep?.id === "ai_pitch") {
        if (aiPitchMessage && !isGeneratingPitch) {
          setShowOptions(true);
        }
        // If still generating, we'll wait for the effect to trigger this
      } else {
        setShowOptions(true);
      }
    }, 300);
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      // Safety check - ensure the step exists
      const stepAtIndex = CONVERSATION_STEPS[currentStepIndex];
      if (!stepAtIndex) {
        console.error("Step at index", currentStepIndex, "is undefined");
        return;
      }

      const currentStepId = stepAtIndex.id;

      // Clear multi-select values for current step
      const newMultiSelectValues = { ...multiSelectValues };
      delete newMultiSelectValues[currentStepId];
      setMultiSelectValues(newMultiSelectValues);

      // Clear completed animation for current step so it can re-animate
      setCompletedAnimations((prev) => {
        const newSet = new Set(prev);
        newSet.delete(currentStepId);
        return newSet;
      });

      setCurrentStepIndex((prev) => prev - 1);
      setShowMessage(false);
      setShowOptions(false);

      // Show previous message after a brief delay
      addTimeout(() => {
        setShowMessage(true);
      }, 300);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        variant: "destructive",
        title: "Logout Error",
        description: "There was an issue logging out. Please try again.",
      });
    }
  };

  const handleResponse = async (
    value:
      | string
      | string[]
      | {
          website?: string;
          brand_color?: string;
          logo_file?: File | null;
          continue?: boolean;
          logo_preview_url?: string;
          textValue?: string;
          firstName?: string;
          lastName?: string;
          organizationName?: string;
          selectedPersona?: string | null;
        },
  ) => {
    // Safety check - ensure currentStep exists
    if (!currentStep) {
      console.error("Current step is undefined");
      return;
    }

    const stepId = currentStep.id;

    // Check if this step is already being submitted
    if (submissionStates[stepId]) {
      console.log(
        `Step ${stepId} is already being submitted, ignoring duplicate request`,
      );
      return;
    }

    // Persona selection step removed - no longer needed in simplified flow

    // Handle welcome form input
    if (
      currentStep.type === "welcome-form" &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      if ("continue" in value) {
        // User clicked Continue button - use modular handler
        await handleWelcomeFormSubmission(stepId, welcomeForm);
        return;
      } else {
        // User is updating form fields - no guard needed for typing
        if ("firstName" in value) {
          setWelcomeForm((prev) => ({
            ...prev,
            firstName: value.firstName || "",
          }));
        }
        if ("lastName" in value) {
          setWelcomeForm((prev) => ({
            ...prev,
            lastName: value.lastName || "",
          }));
        }
        if ("organizationName" in value) {
          setWelcomeForm((prev) => ({
            ...prev,
            organizationName: value.organizationName || "",
          }));
        }
        return;
      }
    }

    // Handle text input (website)
    if (currentStep.type === "text" && typeof value === "string") {
      // User is typing in the input field - no guard needed for typing
      setWebsiteText(value);
      return;
    }

    // Handle text input continue button
    if (
      currentStep.type === "text" &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      "continue" in value
    ) {
      // User clicked Continue button - use modular handler
      const textValue = "textValue" in value ? value.textValue : websiteText;
      await handleTextSubmission(stepId, textValue);
      return;
    }

    // Branding step removed - no longer needed in simplified flow

    // For multi-select questions
    if (currentStep.type === "multi-select" && Array.isArray(value)) {
      // Check if this is the same as what we already have (meaning it's the Continue button)
      const currentValues = multiSelectValues[stepId] || [];
      // Fix: Use slice() to create copies before sorting to avoid mutating React state
      const isFromContinueButton =
        JSON.stringify([...currentValues].sort()) ===
        JSON.stringify([...value].sort());

      if (!isFromContinueButton) {
        // Just updating the selection, not advancing - no guard needed
        setMultiSelectValues({ ...multiSelectValues, [stepId]: value });
        return;
      }
    }

    // AI pitch step removed - no longer needed in simplified flow

    // For other types (buttons, select), use generic handler
    await handleGenericSubmission(stepId, value);
  };

  // Helper function to advance to next step without saving data
  const advanceToNextStep = async () => {
    // Hide options for smooth transition
    setShowOptions(false);

    if (isLastStep) {
      // Process final submission
      setIsProcessing(true);

      // Show completion message
      addTimeout(async () => {
        try {
          await completeOnboarding();
        } catch (error) {
          console.error("Error completing onboarding:", error);
          setIsProcessing(false);
        }
      }, 1000);
    } else {
      // Move to next step
      addTimeout(() => {
        setCurrentStepIndex((prev) => prev + 1);
        setShowMessage(false);
        setShowOptions(false);

        // Show next message after a brief delay
        addTimeout(() => {
          setShowMessage(true);
        }, 500);
      }, 800);
    }
  };

  // Helper function to save step data and advance
  const saveStepData = async (
    stepId: string,
    valueToSave: any,
    newResponses: Record<string, any>,
  ) => {
    // Hide options for smooth transition
    setShowOptions(false);

    // Save data after each step
    try {
      // Handle industry separately
      if (stepId === "industry" && organization?.id) {
        console.log("Saving industry:", valueToSave);
        const { data: industries, error: industriesError } =
          await industryService.getAllIndustries();

        if (industriesError) {
          console.error("Error fetching industries:", industriesError);
        } else if (industries) {
          const industry = industries.find((ind) => ind.name === valueToSave);
          console.log("Found industry:", industry);

          if (industry) {
            const { error: updateError } = await supabase
              .from("organizations")
              .update({ industry_id: industry.id })
              .eq("id", organization.id);

            if (updateError) {
              console.error("Error updating industry:", updateError);
            } else {
              console.log("Industry updated successfully");
            }
          }
        }
      }

      // Handle website separately - save to organization.website
      // Note: For website step, this is already handled in handleTextSubmission before calling saveStepData
      if (
        stepId === "website" &&
        organization?.id &&
        typeof valueToSave === "string"
      ) {
        console.log(
          "Website already saved in handleTextSubmission, skipping duplicate save",
        );
      }

      // Handle branding separately - save to organization
      if (
        stepId === "branding" &&
        organization?.id &&
        typeof valueToSave === "object" &&
        !Array.isArray(valueToSave) &&
        valueToSave !== null
      ) {
        console.log("Saving branding:", valueToSave);
        const brandingUpdate: any = {};

        if ("brand_color" in valueToSave && valueToSave.brand_color) {
          brandingUpdate.brand_color = valueToSave.brand_color;
        }

        // Handle logo upload if there's a file
        if (
          "logo_file" in valueToSave &&
          valueToSave.logo_file &&
          valueToSave.logo_file.size > 0
        ) {
          try {
            const fileExt = valueToSave.logo_file.name.split(".").pop();
            const fileName = `${organization.id}_logo_${Math.random().toString(36).substring(2)}.${fileExt}`;
            const filePath = `organizations/${organization.id}/${fileName}`;

            // Upload file to Supabase Storage
            const { data, error } = await supabase.storage
              .from("image_library")
              .upload(
                `organization_assets/${filePath}`,
                valueToSave.logo_file,
                {
                  cacheControl: "3600",
                  upsert: true,
                },
              );

            if (error) {
              throw error;
            }

            // Get public URL
            const { data: urlData } = await supabase.storage
              .from("image_library")
              .getPublicUrl(`organization_assets/${filePath}`);

            brandingUpdate.logo_url = urlData.publicUrl;
          } catch (error) {
            console.error("Error uploading logo:", error);
          }
        }

        if (Object.keys(brandingUpdate).length > 0) {
          const { error: updateError } = await supabase
            .from("organizations")
            .update(brandingUpdate)
            .eq("id", organization.id);

          if (updateError) {
            console.error("Error updating branding:", updateError);
          } else {
            console.log("Branding updated successfully");
          }
        }
      }

      // Handle persona selection - no database saving, just visual selection
      if (stepId === "persona_selection" && typeof valueToSave === "string") {
        console.log("Selected persona for onboarding:", valueToSave);
        // No database saving - this is just for the visual onboarding experience
      }

      // Handle welcome form separately - use the new atomic service
      if (
        stepId === "welcome" &&
        typeof valueToSave === "object" &&
        !Array.isArray(valueToSave) &&
        valueToSave !== null &&
        "firstName" in valueToSave &&
        "lastName" in valueToSave &&
        "organizationName" in valueToSave
      ) {
        console.log(
          "Saving welcome form data using atomic service:",
          valueToSave,
        );

        if (user?.id && organization?.id) {
          const result = await welcomeFormService.saveWelcomeFormData(
            user.id,
            organization.id,
            {
              firstName: valueToSave.firstName,
              lastName: valueToSave.lastName,
              organizationName: valueToSave.organizationName,
            },
          );

          if (!result.success) {
            console.error("Welcome form save failed:", result.errors);
            // Note: We don't throw here to avoid blocking the user flow
          } else {
            console.log("Welcome form saved successfully:", result.data);
          }
        }
      }

      // Build partial onboarding data based on what we have so far
      const partialData: Partial<OnboardingData> = {};

      // Company size step removed from onboarding flow

      if (newResponses.referral_source) {
        partialData.referral_source =
          newResponses.referral_source as ReferralSource;
      }

      // Build onboarding profile from collected responses (only for existing steps)
      const profileData: Partial<OnboardingProfile> = {};

      if (newResponses.crm_systems) {
        // Store as single value (breaking change from previous array format)
        profileData.crm_systems = [newResponses.crm_systems as CrmSystem];
      }

      if (newResponses.daily_contact_volume) {
        profileData.daily_contact_volume =
          newResponses.daily_contact_volume as DailyContactVolume;
      }

      // Handle AI pitch message
      if (newResponses.ai_pitch && typeof newResponses.ai_pitch === "string") {
        profileData.ai_pitch_message = newResponses.ai_pitch;
      }

      // Only add onboarding_profile if we have profile data
      if (Object.keys(profileData).length > 0) {
        // Get existing profile data from organization and merge with new data
        const existingProfile =
          (organization?.onboarding_profile as OnboardingProfile) || {};
        partialData.onboarding_profile = {
          ...existingProfile,
          ...profileData,
        } as OnboardingProfile;
        console.log(
          "Merged onboarding profile:",
          partialData.onboarding_profile,
        );
      }

      // Always update step to ensure current_step advances (double protection)
      const shouldUpdateStep = true;

      if (shouldUpdateStep) {
        console.log("Saving partial onboarding data:", partialData);

        // Get current step from database to prevent going backwards
        const currentDbStep =
          (organization?.onboarding_meta as { current_step?: number })
            ?.current_step || 1;
        const newStepNumber = currentStepIndex + 1; // Simply advance to next step

        // Only update if we're advancing or staying on the same step, never go backwards
        if (newStepNumber >= currentDbStep) {
          console.log(
            `Advancing from DB step ${currentDbStep} to step ${newStepNumber}`,
          );

          // Call the service directly with proper step numbers for ModernOnboardingFlow
          const result = await onboardingService.updateOnboardingData(
            organization.id,
            partialData,
            newStepNumber, // Use the corrected step calculation
            CONVERSATION_STEPS.length, // Use the actual total steps from ModernOnboardingFlow
            false,
          );

          if (result.error) {
            console.error("Error saving onboarding data:", result.error);
          } else {
            console.log("Onboarding data saved successfully:", result.data);
            // Refresh organization data to prevent stale data issues
            // await refreshProfile();
          }
        } else {
          console.log(
            `Prevented going backwards: DB step ${currentDbStep} > new step ${newStepNumber}`,
          );
          // Don't update the database, but still advance the UI
        }
      }
    } catch (error) {
      console.error("Error saving step data:", error);
      // Continue anyway - we don't want to block the user
    }

    // Use the helper function to advance
    await advanceToNextStep();
  };

  // Show brand extraction loader when setting up organization
  if (isSettingUpOrganization) {
    return (
      <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
        <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

        {/* WhatsApp-style Background Pattern Overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(https://gvaimfxjrywshlnkxrbi.supabase.co/storage/v1/object/public/image_library/chat_backgrounds/Whatsapp-Style-Background.svg)`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            opacity: 0.02,
            zIndex: 1,
            pointerEvents: "none",
            filter: "sepia(0.3) saturate(0.8) hue-rotate(20deg)",
          }}
        />

        {/* Progress indicator */}
        <div className="w-full h-1 bg-gray-100 relative z-10">
          <div
            className="h-full bg-[#ecb22d] transition-all duration-500 ease-out"
            style={{
              width: `${((currentStepIndex + 1) / CONVERSATION_STEPS.length) * 100}%`,
            }}
          ></div>
        </div>

        {/* Header with logo */}
        <div className="text-center py-8 px-4 relative z-10">
          <div className="flex justify-center">
            <img
              src="https://gvaimfxjrywshlnkxrbi.supabase.co/storage/v1/object/public/image_library/public_images/hubflo%20agents%20horizontal%20dark.svg"
              alt="Hubflo Agents Logo"
              className="h-[50px] w-auto"
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center items-center px-4 relative z-10 min-h-screen">
          <div className="max-w-2xl w-full text-center">
            <div
              className="bg-white p-8 rounded-2xl border border-gray-100 max-w-lg mx-auto"
              style={{ boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)" }}
            >
              <p className="text-gray-800 text-lg mb-8 leading-relaxed">
                🤖 Hubflo AI agents are visiting your website to extract
                information and brand assets...
              </p>

              <div className="flex justify-center mb-6">
                <FuturisticLoader size={80} />
              </div>

              <p className="text-gray-500 text-sm">
                This may take a few moments while we analyze your website
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
        <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

        {/* WhatsApp-style Background Pattern Overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/chat_backgrounds/Whatsapp-Style-Background.svg)`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            opacity: 0.02,
            zIndex: 1,
            pointerEvents: "none",
            filter: "sepia(0.3) saturate(0.8) hue-rotate(20deg)",
          }}
        />

        {/* Progress indicator */}
        <div className="w-full h-1 bg-gray-100 relative z-10">
          <div className="h-full bg-[#ecb22d] w-full transition-all duration-1000"></div>
        </div>

        {/* Header with logo */}
        <div className="text-center py-8 px-4 relative z-10">
          <div className="flex justify-center">
            <img
              src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/hubflo%20agents%20horizontal%20dark.svg`}
              alt="Hubflo Agents Logo"
              className="h-[50px] w-auto"
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center items-center px-4 relative z-10">
          <div className="max-w-2xl w-full">
            <ChatBubble
              message="Awesome! 🎉 Your AI agent is being set up based on your preferences. This will just take a moment..."
              isTyping={true}
              showAvatar={true}
            />

            <div className="flex justify-center mt-8">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-[#ecb22d] rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-[#ecb22d] rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-[#ecb22d] rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while we're determining the current step OR if currentStep is undefined
  if (isLoading || !currentStep) {
    return (
      <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
        <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

        {/* WhatsApp-style Background Pattern Overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/chat_backgrounds/Whatsapp-Style-Background.svg)`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            opacity: 0.02,
            zIndex: 1,
            pointerEvents: "none",
            filter: "sepia(0.3) saturate(0.8) hue-rotate(20deg)",
          }}
        />

        {/* Progress indicator */}
        <div className="w-full h-1 bg-gray-100 relative z-10">
          <div
            className="h-full bg-[#ecb22d] transition-all duration-500 ease-out"
            style={{
              width: `${((currentStepIndex + 1) / CONVERSATION_STEPS.length) * 100}%`,
            }}
          ></div>
        </div>

        <div className="flex-1 flex flex-col justify-center items-center px-4 relative z-10 min-h-screen">
          <div className="max-w-2xl w-full text-center">
            <div className="flex justify-center mb-8">
              <img
                src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/hubflo%20agents%20horizontal%20dark.svg`}
                alt="Hubflo Agents Logo"
                className="h-[50px] w-auto"
              />
            </div>

            <div
              className="bg-white p-6 rounded-2xl border border-gray-100 max-w-md mx-auto"
              style={{ boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)" }}
            >
              <p className="text-gray-800 text-base mb-6">
                Loading your onboarding progress...
              </p>

              <div className="flex justify-center">
                <FuturisticLoader size={60} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
      <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

      {/* WhatsApp-style Background Pattern Overlay */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/chat_backgrounds/Whatsapp-Style-Background.svg)`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          opacity: 0.02,
          zIndex: 1,
          pointerEvents: "none",
          filter: "sepia(0.3) saturate(0.8) hue-rotate(20deg)",
        }}
      />

      {/* Progress indicator */}
      <div className="w-full h-1 bg-gray-100 relative z-10">
        <div
          className="h-full bg-[#ecb22d] transition-all duration-500 ease-out"
          style={{
            width: `${((currentStepIndex + 1) / CONVERSATION_STEPS.length) * 100}%`,
          }}
        ></div>
      </div>

      {/* Back button */}
      {!isFirstStep && (
        <div className="absolute top-6 left-6 z-20">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            className="flex items-center gap-2 font-normal"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>
      )}

      {/* Logout button */}
      <div className="absolute top-6 right-6 z-20">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Sign out"
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </div>

      {/* Header with logo */}
      <div className="text-center py-8 px-4 relative z-10">
        <div className="flex justify-center">
          <img
            src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/hubflo%20agents%20horizontal%20dark.svg`}
            alt="Hubflo Agents Logo"
            className="h-[50px] w-auto"
          />
        </div>
      </div>

      {/* Chat conversation */}
      <div className="flex-1 flex justify-center px-4 pb-8 relative z-10">
        <div className="max-w-2xl w-full">
          {/* Current step only - no conversation history */}
          {showMessage && currentStep && (
            <ChatBubble
              message={currentStep.message}
              isTyping={!completedAnimations.has(currentStep.id)}
              onTypingComplete={handleTypingComplete}
              showAvatar={true}
            />
          )}

          {showOptions && currentStep && (
            <div className="mt-4">
              {/* Error message display */}
              {showErrorMessage && currentError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{currentError}</p>
                    </div>
                    <div className="ml-auto pl-3">
                      <button
                        onClick={() => clearError()}
                        className="inline-flex text-red-400 hover:text-red-600"
                      >
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <ResponseOptions
                type={currentStep.type}
                options={currentStep.options}
                onSelect={handleResponse}
                placeholder={currentStep.placeholder}
                selectedValues={
                  currentStep.type === "multi-select"
                    ? multiSelectValues[currentStep.id] || []
                    : undefined
                }
                selectedValue={
                  currentStep.type !== "multi-select"
                    ? responses[currentStep.id]
                    : undefined
                }
                textValue={
                  currentStep.type === "text" ? websiteText : undefined
                }
                brandingData={
                  currentStep.type === "branding" ? brandingData : undefined
                }
                aiPitchReady={
                  currentStep.type === "ai-pitch" ? !!aiPitchMessage : undefined
                }
                isGeneratingPitch={
                  currentStep.type === "ai-pitch"
                    ? isGeneratingPitch
                    : undefined
                }
                welcomeFormData={
                  currentStep.type === "welcome-form" ? welcomeForm : undefined
                }
                personaData={
                  currentStep.type === "persona-cards"
                    ? { personas: ONBOARDING_PERSONAS, selectedPersona }
                    : undefined
                }
                disabled={submissionStates[currentStep.id] || false}
                isSubmitting={
                  currentStep.type === "welcome-form"
                    ? welcomeSubmitting
                    : currentStep.type === "text"
                      ? textSubmitting
                      : currentStep.type === "branding"
                        ? brandingSubmitting
                        : currentStep.type === "ai-pitch"
                          ? aiPitchSubmitting
                          : submissionStates[currentStep.id] || false
                }
                submitText={
                  isStepLoading(currentStep.id)
                    ? getStepLoadingMessage(currentStep.id)
                    : "Continue"
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
